import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  evaluatePilotGates,
  getPilotCoverage,
  getPilotDashboard,
  getPilotDailyScorecard,
  getPilotStatus,
  listPilotSeedBatches,
} from "../../api/clientVendorDirectoryPilotApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

function MetricCard({ label, value }) {
  return (
    <div className="cvd-metric-card">
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">{value ?? "—"}</div>
    </div>
  );
}

function PilotShell({ base, children }) {
  return (
    <div className="cvd-admin">
      <ClientVendorDirectorySubnav />
      <nav className="cvd-subnav" aria-label="Pilot">
        <NavLink to={base} end>Panel</NavLink>
        <NavLink to={`${base}/puertas`}>Puertas</NavLink>
        <NavLink to={`${base}/lotes`}>Lotes</NavLink>
        <NavLink to={`${base}/cobertura`}>Cobertura</NavLink>
        <NavLink to={`${base}/scorecard`}>Scorecard</NavLink>
      </nav>
      {children}
    </div>
  );
}

function OverviewPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t } = useLocale();
  const paths = useClientPortalPaths();
  const base = `${serviciosBase}/piloto`;
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getPilotDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PilotShell base={base}>
      <h1 className="cvd-admin__title">{t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.pilotPage.title")}</h1>
      {loading ? <ClientPortalLoading message={t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.pilotPage.loading")} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && dashboard ? (
        <>
          <div className="cvd-metric-grid">
            <MetricCard label="Etapa" value={dashboard.featureStatus?.rolloutStage} />
            <MetricCard label="Tráfico %" value={dashboard.featureStatus?.trafficPercentage} />
            <MetricCard label="Proveedores publicados" value={dashboard.supply?.publishedPilotVendors} />
            <MetricCard label="Lotes publicados" value={dashboard.supply?.publishedBatches} />
            <MetricCard label="Contextos listos" value={dashboard.coverage?.launchReady} />
            <MetricCard label="Puertas aprobadas" value={dashboard.launchGates?.passed} />
          </div>
          {dashboard.alerts?.length ? (
            <div className="cvd-panel" role="alert">
              <h2>Alertas</h2>
              <ul>
                {dashboard.alerts.map((alert) => (
                  <li key={alert.code}>
                    <strong>{alert.severity}</strong>: {alert.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </PilotShell>
  );
}

function GatesPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const paths = useClientPortalPaths();
  const base = `${serviciosBase}/piloto`;
  const [gates, setGates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await evaluatePilotGates(clientViewId);
      setGates(body.gates || []);
      setSummary(body.summary || null);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PilotShell base={base}>
      <h1 className="cvd-admin__title">Puertas de lanzamiento</h1>
      {loading ? <ClientPortalLoading message="Evaluando puertas…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading ? (
        <>
          {summary ? (
            <p>
              {summary.passed}/{summary.total} aprobadas — activación{" "}
              {summary.canActivate ? "permitida" : "bloqueada"}
            </p>
          ) : null}
          <table className="cvd-table">
            <thead>
              <tr>
                <th>Puerta</th>
                <th>Estado</th>
                <th>Crítica</th>
                <th>Evidencia</th>
              </tr>
            </thead>
            <tbody>
              {gates.map((gate) => (
                <tr key={gate.gateKey}>
                  <td>{gate.gateKey}</td>
                  <td>{gate.status}</td>
                  <td>{gate.isCritical ? "Sí" : "No"}</td>
                  <td>{gate.evidence || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </PilotShell>
  );
}

function BatchesPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const paths = useClientPortalPaths();
  const base = `${serviciosBase}/piloto`;
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    listPilotSeedBatches(clientViewId)
      .then((body) => setBatches(body.batches || []))
      .catch((error) => setErrorMessage(error.message))
      .finally(() => setLoading(false));
  }, [clientViewId]);

  return (
    <PilotShell base={base}>
      <h1 className="cvd-admin__title">Lotes de siembra</h1>
      {loading ? <ClientPortalLoading message="Cargando lotes…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}
      {!loading ? (
        <table className="cvd-table">
          <thead>
            <tr>
              <th>Lote</th>
              <th>Estado</th>
              <th>Publicados</th>
              <th>Creado</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr key={batch.id}>
                <td>{batch.name}</td>
                <td>{batch.status}</td>
                <td>{batch.publishedCount}</td>
                <td>{new Date(batch.createdAt).toLocaleDateString("es-CO")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </PilotShell>
  );
}

function CoveragePage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const paths = useClientPortalPaths();
  const base = `${serviciosBase}/piloto`;
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPilotCoverage(clientViewId)
      .then((body) => setContexts(body.contexts || []))
      .finally(() => setLoading(false));
  }, [clientViewId]);

  return (
    <PilotShell base={base}>
      <h1 className="cvd-admin__title">Cobertura piloto</h1>
      {loading ? <ClientPortalLoading message="Cargando cobertura…" /> : null}
      {!loading ? (
        <table className="cvd-table">
          <thead>
            <tr>
              <th>Categoría</th>
              <th>Municipio</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {contexts.map((ctx) => (
              <tr key={ctx.id}>
                <td>{ctx.categoryKey}</td>
                <td>{ctx.municipalityKey}</td>
                <td>{ctx.readinessState}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </PilotShell>
  );
}

function ScorecardPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const paths = useClientPortalPaths();
  const base = `${serviciosBase}/piloto`;
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPilotDailyScorecard(clientViewId)
      .then((body) => setScorecard(body.scorecard))
      .finally(() => setLoading(false));
  }, [clientViewId]);

  return (
    <PilotShell base={base}>
      <h1 className="cvd-admin__title">Scorecard diario</h1>
      {loading ? <ClientPortalLoading message="Generando scorecard…" /> : null}
      {!loading && scorecard ? (
        <pre className="cvd-code-block">{JSON.stringify(scorecard.metrics, null, 2)}</pre>
      ) : null}
    </PilotShell>
  );
}

export default function ClientVendorDirectoryPilotHubPage() {
  return (
    <Routes>
      <Route index element={<OverviewPage />} />
      <Route path="puertas" element={<GatesPage />} />
      <Route path="lotes" element={<BatchesPage />} />
      <Route path="cobertura" element={<CoveragePage />} />
      <Route path="scorecard" element={<ScorecardPage />} />
    </Routes>
  );
}
